import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useHistoryCustomBack } from 'weblite-router'
import { styled, IconButton, Typography } from '@mui/material'
import { LIMITS } from '@weblite/permission'
import { EmojiPicker } from 'weblite-web-emoji'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import i18next from 'i18next'
import { useMultiSelectList } from '$common/multi-select-searchable-list-page'
import {
  RoomListItem,
  useRoomsWithSendMessagePermission,
} from '$logic/room-list/room-list.react-hook'
import {
  showForwardMenuPermissionSnackbar,
  showSelectionLimitSnackbar,
} from '$comp/snackbars'
import {
  FORWARD_CAPTION_HEIGHT,
  ForwardCaption,
} from '$comp/_app/components/leftPanel/components/forward-list/components/forward-caption'
import { editorAPI } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import { useWindowHeight } from '$func/window.react-hook'
import { vibrate } from '$func/vibrate'
import { emojiI18n } from '$setup/emoji'
import { BottomDrawer } from '$common/bottom-drawer'
import { DebouncedUnControlledSearchInput } from '$common/input'
import { ForwardMenuSkeleton } from '$comp/modals/forward-menu/components/forward-menu-skeleton/forward-menu-skeleton'
import { useSearchedLocalRooms } from '$logic/room-list'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { CloseIcon } from '$common/icons'
import { ListRenderer, SearchListRenderer } from './lists'
import { t } from '$i18n'

const BASE_FORWARD_LIST_LIMIT = LIMITS.forwardDestinationCount
export const SelectionMapContext = createContext({})
export const ForwardMenuSearchQueryContext = createContext('')
export const FORWARD_MENU_DEFAULT_HEIGHT = 420

const StyledBottomDrawer = styled(BottomDrawer)`
  & .MuiDrawer-paper {
    height: 60%;
  }

  &.is-desktop {
    & .MuiDialog-paper {
      height: 640px;
      min-width: 400px;
    }
  }
`

const Header = styled('div')`
  display: flex;
  gap: 8px;

  width: 100%;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral300};

  .is-desktop {
    padding-right: 8px;
  }
`

const StyledSearchInput = styled(DebouncedUnControlledSearchInput)`
  padding: 0;
  flex-grow: 1;
`

interface Props {
  onConfirm: (selectedRooms: string[], caption?: string) => Promise<any>
  forwardLimit?: number
  isOpen?: boolean
  disableBack?: boolean
}

export function ForwardMenu({
  forwardLimit,
  onConfirm,
  isOpen,
  disableBack = false,
}: Props) {
  const isDesktop = useIsDesktopSize()
  const [searchQuery, setSearchQuery] = useState<string>('')

  const localSearchResult = useSearchedLocalRooms(searchQuery)

  const { items, loadingItems, loadingPermissions } =
    useRoomsWithSendMessagePermission()

  const defaultRoomsIds = useMemo(
    () => items.map(({ chatId }) => chatId),
    [items],
  )

  const localSearchRoomItems = useMemo(
    () =>
      searchQuery
        ? localSearchResult?.roomIds?.map(chatId => ({
            chatId,
          }))
        : [],
    [localSearchResult?.roomIds, searchQuery],
  ) as RoomListItem[]

  const windowHeight = useWindowHeight()
  const listHeight = windowHeight * 0.6
  const FORWARD_LIST_LIMIT = forwardLimit || BASE_FORWARD_LIST_LIMIT

  const selectionCountRef = useRef(0)
  const { selectedItems, selectionMap, setSelection, hasSelection } =
    useMultiSelectList({ items, selectionPrimaryKey: 'chatId' })

  const availableHeight =
    listHeight - (hasSelection ? FORWARD_CAPTION_HEIGHT : 0)

  useEffect(() => {
    selectionCountRef.current =
      Object.values(selectionMap).filter(Boolean).length
  }, [selectionMap])

  const onItemSelectedChange = useCallback(
    (selected: boolean, chatId: string) => {
      if (selected && !defaultRoomsIds.includes(chatId)) {
        showForwardMenuPermissionSnackbar()
        return
      }

      if (selectionCountRef.current < FORWARD_LIST_LIMIT || !selected) {
        setSelection(chatId, selected)
        if (selected) vibrate()
      } else showSelectionLimitSnackbar(FORWARD_LIST_LIMIT)
    },
    [defaultRoomsIds],
  )

  useHistoryCustomBack(() => {
    if (!disableBack) closeCurrentModal()
  }, [disableBack])

  const hasItems = Boolean(
    searchQuery ? localSearchRoomItems?.length : items?.length,
  )

  return (
    <StyledBottomDrawer
      className={isDesktop ? 'is-desktop' : ''}
      onClose={() => {
        if (!disableBack) closeCurrentModal()
      }}
      isOpen={!!isOpen}
    >
      <Header className={isDesktop ? 'is-desktop' : undefined}>
        {isDesktop && !disableBack && (
          <IconButton onClick={closeCurrentModal}>
            <CloseIcon sx={{ color: 'neutral700' }} />
          </IconButton>
        )}

        <StyledSearchInput
          placeholder={t('forwardAppBar.title')}
          onQueryChange={q => {
            setSearchQuery(q)
          }}
        />
      </Header>

      {loadingPermissions || loadingItems ? (
        <ForwardMenuSkeleton height={availableHeight} />
      ) : hasItems ? (
        <SelectionMapContext.Provider value={selectionMap}>
          <ForwardMenuSearchQueryContext.Provider value={searchQuery}>
            {searchQuery ? (
              <SearchListRenderer
                items={localSearchRoomItems}
                selectionMap={selectionMap}
                onSelectedChange={onItemSelectedChange}
              />
            ) : (
              <ListRenderer
                items={items}
                selectionMap={selectionMap}
                onSelectedChange={onItemSelectedChange}
              />
            )}

            <EmojiPicker
              i18n={emojiI18n}
              addEmoji={editorAPI.insertText}
              locale={i18next.language}
            />
          </ForwardMenuSearchQueryContext.Provider>
        </SelectionMapContext.Provider>
      ) : searchQuery ? (
        <Typography variant="body2" margin="auto">
          {t('emptyState.noResultFound')}
        </Typography>
      ) : (
        <Typography variant="body2" margin="auto">
          {t('emptyState.noContactFound')}
        </Typography>
      )}

      {hasSelection && (
        <ForwardCaption
          selectionCount={selectedItems.length}
          onSubmit={async (caption: string) => {
            await closeCurrentModal()
            await onConfirm(
              selectedItems.map(({ chatId }) => chatId),
              caption,
            )
          }}
        />
      )}
    </StyledBottomDrawer>
  )
}

ForwardMenu.defaultProps = {
  forwardLimit: null,
}
